import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPreview() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        console.log('Blog posts fetched:', response.data);
        setPosts(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#F5F5F5]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-[#666]">Loading insights...</p>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#F5F5F5]" id="blog">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#222] mb-4">Latest Insights</h2>
            <p className="text-[#666] text-base max-w-2xl mx-auto">
              Check back soon for expert perspectives on workspace design, productivity, and business growth.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#F5F5F5]" id="blog" data-testid="blog-preview-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#222] mb-3 sm:mb-4">
            Latest Insights
          </h2>
          <p className="text-[#666] text-sm sm:text-base max-w-2xl mx-auto px-4">
            Expert perspectives on workspace design, productivity, and business growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {posts.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid={`blog-card-${index}`}
            >
              <div className="relative h-40 sm:h-48">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                  <span className="px-2 sm:px-3 py-1 bg-[#E12429] text-white text-xs font-semibold rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex items-center space-x-2 text-xs sm:text-sm text-[#666] mb-2 sm:mb-3">
                  <Calendar size={14} />
                  <span>{formatDate(post.created_at)}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#222] mb-2 sm:mb-3 line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-[#666] text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center text-[#E12429] font-semibold text-xs sm:text-sm">
                  Read More
                  <ArrowRight size={16} className="ml-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 lg:mt-12">
          <Link to="/blog">
            <button className="btn-primary" data-testid="view-all-blog-button">
              View All Articles
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}